import React from 'react';
import { AskRequest, EntityLink, ResolvedLink } from '@hone-automation/common';
import { useTransactionDetails, EnhancedAskRequest } from 'hooks/useTransactionDetails';
import useFetchLinks from 'hooks/useFetchLinks';
import { useParams } from 'react-router-dom';
import './InfoPanel.scss';
import { formatAmount } from 'presentation/components/KSExport/ksExporterUtils';
interface InfoPanelProps {
  request: AskRequest;
  trxURL?: string;
  className?: string;
}

interface InfoDetailsItemProps {
  label: string;
  value: string | number | null | undefined;
  money?: boolean;
  highlight?: boolean;
}

/**
 * InfoDetailsItem component for displaying label-value pairs
 */
const InfoDetailsItem: React.FC<InfoDetailsItemProps> = ({ label, value, money = false, highlight = false }) => {
  const formattedValue = money && value !== '-' ? formatAmount(value as number, 2, true) : value || 'N/A';
  return (
    <div className={`info-details-item ${highlight ? 'highlight' : ''}`}>
      <div className="info-details-label">{label}</div>
      <div className={`info-details-value ${money ? 'money' : ''} ${label === 'Type' ? 'capitalize' : ''}`}>
        {formattedValue}
      </div>
    </div>
  );
};

/**
 * Panel content for displaying request information - can be used in both desktop and mobile views
 */
const InfoPanel: React.FC<InfoPanelProps> = ({ request, trxURL, className }) => {
  const { locationId } = useParams();

  // Use our transaction details hook to get formatted data
  const details = useTransactionDetails(request as EnhancedAskRequest);

  // Fetch links data
  const { data: links, isLoading: linksLoading, error: linksError } = useFetchLinks(request, locationId);

  if (!details) return <div>Loading request details...</div>;

  const {
    requestType,
    isAskRequest,
    isFlaggedTransaction,
    isFlaggedCell,
    isCustomRequest,
    createdDate,
    trxDate,
    createdByUser,
    transactionName,
    transactionMemo,
    transactionAccount,
    transactionAmount,
    flaggedAmount,
    hasBlockerPriority,
    transactionType,
  } = details;

  return (
    <div className={`info-panel ${className || ''}`}>
      <div className="info-panel-section">
        <h4 className="info-panel-title">Request Information</h4>

        <div className="info-details-list">
          {/* Custom Request view */}
          {isCustomRequest && (
            <>
              <InfoDetailsItem label="Type" value={requestType} />
              <InfoDetailsItem label="Created on" value={createdDate} />
              <InfoDetailsItem label="Created by" value={createdByUser} />
              {hasBlockerPriority && <InfoDetailsItem highlight label="Priority" value="Urgent" />}
            </>
          )}

          {/* Flagged Cell view */}
          {isFlaggedCell && (
            <>
              <InfoDetailsItem label="Type" value={'Flagged Report Cell'} />
              <InfoDetailsItem label="Created on" value={createdDate} />
              <InfoDetailsItem label="Created by" value={createdByUser} />
              <InfoDetailsItem label="Amount" value={flaggedAmount || transactionAmount} money />
              {hasBlockerPriority && <InfoDetailsItem highlight label="Priority" value="Urgent" />}
            </>
          )}

          {/* Transaction Request view */}
          {(isFlaggedTransaction || isAskRequest) && (
            <>
              <InfoDetailsItem label="Request Type" value={requestType} />
              <InfoDetailsItem label="Transaction Type" value={transactionType} />
              <InfoDetailsItem label="Original Account" value={transactionAccount} />
              <InfoDetailsItem label="Created on" value={createdDate} />
              <InfoDetailsItem label="Created by" value={createdByUser} />
              <InfoDetailsItem label="Transaction Date" value={trxDate} />
              <InfoDetailsItem label="Name" value={transactionName} />
              <InfoDetailsItem label="Description" value={transactionMemo} />
              <InfoDetailsItem label="Amount" value={flaggedAmount || transactionAmount} money />
              {hasBlockerPriority && <InfoDetailsItem highlight label="Priority" value="Urgent" />}
            </>
          )}
        </div>

        {/* {trxURL && (
          <div className="info-panel-actions">
            <button className="info-panel-button" onClick={() => navigator.clipboard.writeText(trxURL)}>
              Copy Link
            </button>
            Uncomment if needed
            <a 
              className="info-panel-button" 
              href={trxURL} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Open in New Tab
            </a>
          </div>
        )} */}
      </div>

      {/* Links Section */}
      {(linksLoading || (links && links.length > 0)) && (
        <div className="info-panel-section">
          <h4 className="info-panel-title">Related Links</h4>
          <div className="info-details-list">
            {linksLoading && <div className="links-loading">Loading links...</div>}
            {linksError && <div className="links-error">Error loading links</div>}
            {links && links.length > 0 && (
              <>
                {(links as ResolvedLink<EntityLink>[]).map((link, index) => {
                  const isValid = link.valid;
                  return (
                    <div key={index} className={`info-link-item ${!isValid ? 'invalid' : ''}`}>
                      {!isValid && (
                        <div className="link-invalid-notice">Link is no longer valid or has been updated</div>
                      )}

                      {link.reportTitle && <InfoDetailsItem label="Report Title" value={link.reportTitle} />}

                      {link.sectionPath && <InfoDetailsItem label="Report Section" value={link.sectionPath} />}

                      {link.sectionAmount !== undefined && link.sectionAmount !== null && (
                        <InfoDetailsItem label="Report Amount" value={link.sectionAmount} money />
                      )}

                      {link.dataDateRange && (
                        <InfoDetailsItem
                          label="Date Range"
                          value={`${link.dataDateRange.start || 'N/A'} - ${link.dataDateRange.end || 'N/A'}`}
                        />
                      )}

                      {link.attachments && link.attachments.length > 0 && (
                        <InfoDetailsItem
                          label="Attachments"
                          value={`${link.attachments.length} file${link.attachments.length > 1 ? 's' : ''}`}
                        />
                      )}
                    </div>
                  );
                })}
              </>
            )}
            {!linksLoading && (!links || links.length === 0) && (
              <div className="links-empty">No related links found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoPanel;
